import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import * as api from "./adminApi";
import { AdminShell, Btn, Card, Field, InlineForm, Row, Sel, Text, useAsync } from "./components";

export function LearnersPage() {
  const orgs = useAsync(api.listOrganisations, []);
  const learners = useAsync(api.listLearners, []);
  const programmes = useAsync(api.listProgrammes, []);

  const clientOrgs = (orgs.data ?? []).filter((o) => !o.isWithAWill);

  const [logoUploadingId, setLogoUploadingId] = useState<string | null>(null);
  const [logoError, setLogoError] = useState<string | null>(null);
  const logoInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  async function uploadLogo(orgId: string, file: File) {
    setLogoUploadingId(orgId);
    setLogoError(null);
    try {
      await api.uploadOrganisationLogo(orgId, file);
      orgs.reload();
    } catch (e) {
      setLogoError(e instanceof Error ? e.message : String(e));
    } finally {
      setLogoUploadingId(null);
    }
  }

  const [orgName, setOrgName] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [orgId, setOrgId] = useState("");
  const [invite, setInvite] = useState<string | null>(null);

  const [assignLearner, setAssignLearner] = useState("");
  const [assignProg, setAssignProg] = useState("");
  const [assignMsg, setAssignMsg] = useState<string | null>(null);

  return (
    <AdminShell crumbs={[{ label: "Admin", to: "/admin" }]} title="Learners & assignments">
      <p>
        <Link to="/admin">← Programmes</Link>
      </p>

      <Card>
        <strong>Client organisations</strong>
        <p style={{ color: "#666", fontSize: "0.85rem", marginTop: "0.2rem" }}>
          A logo here appears on that organisation's learners' certificates, alongside WithAWill's.
        </p>
        {logoError && <p style={{ color: "#b00020", fontSize: "0.85rem" }}>{logoError}</p>}
        {clientOrgs.length === 0 && <p style={{ color: "#666" }}>No client organisations yet.</p>}
        {clientOrgs.map((o) => (
          <Row key={o.id}>
            <span>{o.name}</span>
            <span style={{ display: "flex", gap: "0.6rem", alignItems: "center" }}>
              <span style={{ color: "#888", fontSize: "0.8rem" }}>
                {o._count.users} learners · {o._count.enrollments} enrolments ·{" "}
                {o.logoBlobRef ? "logo uploaded" : "no logo"}
              </span>
              <input
                ref={(el) => {
                  logoInputRefs.current[o.id] = el;
                }}
                type="file"
                accept=".png,.jpg,.jpeg"
                style={{ display: "none" }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) uploadLogo(o.id, file);
                  e.target.value = "";
                }}
              />
              <Btn
                kind="ghost"
                disabled={logoUploadingId === o.id}
                onClick={() => logoInputRefs.current[o.id]?.click()}
              >
                {logoUploadingId === o.id
                  ? "Uploading…"
                  : o.logoBlobRef
                    ? "Replace logo"
                    : "Upload logo"}
              </Btn>
            </span>
          </Row>
        ))}
        <div style={{ marginTop: "0.8rem" }}>
          <InlineForm
            submitLabel="Add organisation"
            onSubmit={async () => {
              await api.createOrganisation({ name: orgName });
              setOrgName("");
              orgs.reload();
            }}
          >
            <Field label="Organisation name">
              <Text value={orgName} onChange={(e) => setOrgName(e.target.value)} required />
            </Field>
          </InlineForm>
        </div>
      </Card>

      <Card>
        <strong>Learners</strong>
        {(learners.data ?? []).length === 0 && <p style={{ color: "#666" }}>No learners yet.</p>}
        {(learners.data ?? []).map((l) => (
          <Row key={l.id}>
            <span>
              {l.displayName} <span style={{ color: "#888" }}>· {l.email}</span>
            </span>
            <span style={{ color: "#888", fontSize: "0.8rem" }}>
              {l.organisation.name} · {l.status.toLowerCase()} · {l._count.enrollments} enrolments
            </span>
          </Row>
        ))}
        <div style={{ marginTop: "0.8rem" }}>
          <InlineForm
            submitLabel="Create learner & invite"
            onSubmit={async () => {
              const r = await api.createLearner({
                email,
                displayName: name,
                organisationId: orgId,
              });
              setInvite(r.inviteUrl);
              setEmail("");
              setName("");
              learners.reload();
              orgs.reload();
            }}
          >
            <Field label="Full name">
              <Text value={name} onChange={(e) => setName(e.target.value)} required />
            </Field>
            <Field label="Email">
              <Text
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </Field>
            <Field label="Organisation">
              <Sel value={orgId} onChange={(e) => setOrgId(e.target.value)} required>
                <option value="">Select…</option>
                {clientOrgs.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </Sel>
            </Field>
          </InlineForm>
          {invite && (
            <p style={{ fontSize: "0.85rem", marginTop: "0.6rem" }}>
              Invite link (send to the learner):
              <br />
              <code style={{ wordBreak: "break-all" }}>{invite}</code>
            </p>
          )}
        </div>
      </Card>

      <Card>
        <strong>Assign a programme</strong>
        <p style={{ color: "#666", fontSize: "0.85rem", marginTop: "0.2rem" }}>
          Creates an enrolment per published track. The learner is emailed (queued).
        </p>
        <InlineForm
          submitLabel="Assign"
          onSubmit={async () => {
            const r = await api.assignProgramme({
              learnerId: assignLearner,
              programmeId: assignProg,
            });
            setAssignMsg(`${r.enrollmentsCreated} enrolment(s) created`);
            learners.reload();
          }}
        >
          <Field label="Learner">
            <Sel value={assignLearner} onChange={(e) => setAssignLearner(e.target.value)} required>
              <option value="">Select…</option>
              {(learners.data ?? []).map((l) => (
                <option key={l.id} value={l.id}>
                  {l.displayName} ({l.email})
                </option>
              ))}
            </Sel>
          </Field>
          <Field label="Programme">
            <Sel value={assignProg} onChange={(e) => setAssignProg(e.target.value)} required>
              <option value="">Select…</option>
              {(programmes.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.title} {p.status !== "PUBLISHED" ? "(draft)" : ""}
                </option>
              ))}
            </Sel>
          </Field>
        </InlineForm>
        {assignMsg && <p style={{ color: "#0a7d33", fontSize: "0.85rem" }}>{assignMsg}</p>}
      </Card>
    </AdminShell>
  );
}
